export type AnniversaryEntityType = 'radio' | 'programa' | 'locutor' | 'club' | 'usuario';

export interface AnniversaryTypeConfig {
  key: AnniversaryEntityType;
  label: string;
  icon: string;
  color: string;
  bgColor: string;
  borderColor: string;
  gradient: string;
  description: string;
  celebrationVerb: string;
}

export interface AnniversaryEntry {
  id: string;
  entityType: AnniversaryEntityType;
  entityName: string;
  entitySlug: string;
  entityPhoto: string;
  years: number;
  anniversaryDate: string;
  anniversaryDateFormatted: string;
  foundedDate: string;
  description: string;
  felicitaciones: number;
  compartidos: number;
  comentarios: number;
  comments: AnniversaryComment[];
  relatedInfo: string;
  relatedLink: string;
  isToday: boolean;
  isThisWeek: boolean;
  isThisMonth: boolean;
  daysUntil: number;
  hasCelebrated: boolean;
}

export interface AnniversaryComment {
  id: string;
  userName: string;
  userPhoto: string;
  text: string;
  dateRelative: string;
}

export interface AnniversaryStats {
  totalToday: number;
  totalThisWeek: number;
  totalThisMonth: number;
  totalUpcoming: number;
  totalCelebratedThisYear: number;
  mostCelebratedToday: string;
  totalFelicitacionesHoy: number;
}

export interface AnniversaryRule {
  id: number;
  title: string;
  description: string;
  icon: string;
  color: string;
  bgColor: string;
}

export const anniversaryTypeConfigs: AnniversaryTypeConfig[] = [
  {
    key: 'radio',
    label: 'Radio',
    icon: 'ri-broadcast-line',
    color: 'text-rose-600',
    bgColor: 'bg-rose-50',
    borderColor: 'border-rose-200',
    gradient: 'from-rose-400 to-pink-500',
    description: 'El día que la emisora salió al aire por primera vez.',
    celebrationVerb: 'cumple',
  },
  {
    key: 'programa',
    label: 'Programa',
    icon: 'ri-calendar-event-line',
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
    borderColor: 'border-violet-200',
    gradient: 'from-violet-400 to-purple-500',
    description: 'El aniversario de ese programa que no te perdés nunca.',
    celebrationVerb: 'cumple',
  },
  {
    key: 'locutor',
    label: 'Locutor',
    icon: 'ri-mic-line',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    gradient: 'from-amber-400 to-orange-500',
    description: 'Los años que lleva esa voz acompañándote al aire.',
    celebrationVerb: 'cumple',
  },
  {
    key: 'club',
    label: 'Club',
    icon: 'ri-team-line',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    gradient: 'from-emerald-400 to-teal-500',
    description: 'El cumpleaños del club donde pertenecés.',
    celebrationVerb: 'cumple',
  },
  {
    key: 'usuario',
    label: 'Usuario',
    icon: 'ri-user-heart-line',
    color: 'text-sky-600',
    bgColor: 'bg-sky-50',
    borderColor: 'border-sky-200',
    gradient: 'from-sky-400 to-cyan-500',
    description: 'Un año más de ser parte de la comunidad RFW.',
    celebrationVerb: 'está de cumpleaños',
  },
];

export const anniversaries: AnniversaryEntry[] = [
  // ---- RADIOS ----
  {
    id: 'ann-r-001',
    entityType: 'radio',
    entityName: 'La Mega 107.3 FM',
    entitySlug: 'la-mega-107-3',
    entityPhoto: 'https://readdy.ai/api/search-image?query=Vintage%20radio%20station%20building%20facade%20with%20broadcast%20antenna%20warm%20sunset%20lighting%20nostalgic%20atmosphere%20urban%20landscape%20editorial%20photography%20urban%20radio%20heritage%20celebration&width=400&height=300&seq=ann-radio-lamega&orientation=landscape',
    years: 18,
    anniversaryDate: '2026-06-21',
    anniversaryDateFormatted: '21 de Junio',
    foundedDate: '2008-06-21',
    description: '18 años siendo la emisora juvenil más escuchada del país. Dos décadas de música, entretenimiento y comunidad.',
    felicitaciones: 842,
    compartidos: 156,
    comentarios: 94,
    comments: [
      { id: 'c1', userName: 'María José Herrera', userPhoto: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20young%20Latina%20woman%20mid%20twenties%20warm%20friendly%20smile%20clean%20studio%20background%20soft%20natural%20lighting&width=100&height=100&seq=ann-comment-mj&orientation=squarish', text: '¡Felices 18 años! Los escucho desde que tengo 15. Son mi compañía diaria.', dateRelative: 'Hace 2 horas' },
      { id: 'c2', userName: 'Carlos Vega', userPhoto: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20young%20Colombian%20male%20late%20twenties%20focused%20expression%20clean%20studio%20background%20warm%20lighting&width=100&height=100&seq=ann-comment-cv&orientation=squarish', text: 'La Mega es familia. Feliz aniversario a todo el equipo.', dateRelative: 'Hace 1 hora' },
      { id: 'c3', userName: 'Valentina Ruiz', userPhoto: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20young%20Latina%20woman%20early%20twenties%20bright%20natural%20smile%20clean%20studio%20lighting&width=100&height=100&seq=ann-comment-vr&orientation=squarish', text: '¡18 años! Que vengan muchos más. La mejor radio de todas.', dateRelative: 'Hace 30 min' },
    ],
    relatedInfo: 'Emisora Juvenil · 107.3 FM',
    relatedLink: '/radio/107.3',
    isToday: true,
    isThisWeek: true,
    isThisMonth: true,
    daysUntil: 0,
    hasCelebrated: false,
  },
  {
    id: 'ann-r-002',
    entityType: 'radio',
    entityName: 'Rock & Pop 95.1 FM',
    entitySlug: 'rock-and-pop-95-1',
    entityPhoto: 'https://readdy.ai/api/search-image?query=Rock%20music%20radio%20station%20studio%20with%20guitars%20on%20wall%20neon%20signs%20warm%20amber%20lighting%20retro%20modern%20aesthetic%20editorial%20photography&width=400&height=300&seq=ann-radio-rockpop&orientation=landscape',
    years: 12,
    anniversaryDate: '2026-06-24',
    anniversaryDateFormatted: '24 de Junio',
    foundedDate: '2014-06-24',
    description: '12 años de rock, de cultura, de ser la voz de una generación que nunca baja el volumen.',
    felicitaciones: 315,
    compartidos: 87,
    comentarios: 42,
    comments: [
      { id: 'c4', userName: 'Diego López', userPhoto: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20Argentine%20male%20early%20thirties%20relaxed%20confident%20expression%20clean%20studio%20background%20modern%20lighting&width=100&height=100&seq=ann-comment-dl&orientation=squarish', text: 'La radio que me enseñó a amar el rock. Feliz aniversario.', dateRelative: 'Hace 30 min' },
    ],
    relatedInfo: 'Rock & Clásicos · 95.1 FM',
    relatedLink: '/radio/95.1',
    isToday: false,
    isThisWeek: true,
    isThisMonth: true,
    daysUntil: 3,
    hasCelebrated: false,
  },
  {
    id: 'ann-r-003',
    entityType: 'radio',
    entityName: 'Tropicalísima 102.7 FM',
    entitySlug: 'tropicalisima-102-7',
    entityPhoto: 'https://readdy.ai/api/search-image?query=Tropical%20music%20radio%20station%20with%20colorful%20festive%20decorations%20palm%20tree%20silhouette%20Caribbean%20sunset%20atmosphere%20warm%20joyful%20celebration&width=400&height=300&seq=ann-radio-tropicalisima&orientation=landscape',
    years: 7,
    anniversaryDate: '2026-06-28',
    anniversaryDateFormatted: '28 de Junio',
    foundedDate: '2019-06-28',
    description: '7 años llevando el sabor tropical a cada rincón. La emisora que te hace bailar.',
    felicitaciones: 198,
    compartidos: 53,
    comentarios: 28,
    comments: [],
    relatedInfo: 'Música Tropical · 102.7 FM',
    relatedLink: '/radio/102.7',
    isToday: false,
    isThisWeek: true,
    isThisMonth: true,
    daysUntil: 7,
    hasCelebrated: false,
  },
  {
    id: 'ann-r-004',
    entityType: 'radio',
    entityName: 'Radio Éxito 94.3 FM',
    entitySlug: 'radio-exito-94-3',
    entityPhoto: 'https://readdy.ai/api/search-image?query=Modern%20contemporary%20radio%20station%20studio%20with%20polished%20professional%20aesthetic%20clean%20lines%20warm%20lighting%20success%20celebration%20editorial%20photography&width=400&height=300&seq=ann-radio-exito&orientation=landscape',
    years: 25,
    anniversaryDate: '2026-07-05',
    anniversaryDateFormatted: '5 de Julio',
    foundedDate: '2001-07-05',
    description: 'Un cuarto de siglo conectando generaciones. 25 años de éxitos, noticias y comunidad.',
    felicitaciones: 1250,
    compartidos: 340,
    comentarios: 187,
    comments: [],
    relatedInfo: 'Música Variada · 94.3 FM',
    relatedLink: '/radio/94.3',
    isToday: false,
    isThisWeek: false,
    isThisMonth: true,
    daysUntil: 14,
    hasCelebrated: false,
  },
  {
    id: 'ann-r-005',
    entityType: 'radio',
    entityName: 'Onda Cero 104.5 FM',
    entitySlug: 'onda-cero-104-5',
    entityPhoto: 'https://readdy.ai/api/search-image?query=News%20and%20talk%20radio%20station%20elegant%20modern%20studio%20sophisticated%20lighting%20professional%20broadcast%20environment%20editorial%20photography&width=400&height=300&seq=ann-radio-ondacero&orientation=landscape',
    years: 3,
    anniversaryDate: '2026-06-22',
    anniversaryDateFormatted: '22 de Junio',
    foundedDate: '2023-06-22',
    description: '3 años de información, debate y análisis. La emisora joven que ya marca agenda.',
    felicitaciones: 87,
    compartidos: 31,
    comentarios: 15,
    comments: [],
    relatedInfo: 'Noticias & Debate · 104.5 FM',
    relatedLink: '/radio/104.5',
    isToday: false,
    isThisWeek: true,
    isThisMonth: true,
    daysUntil: 1,
    hasCelebrated: false,
  },

  // ---- PROGRAMAS ----
  {
    id: 'ann-p-001',
    entityType: 'programa',
    entityName: 'Carrusel Deportivo',
    entitySlug: 'carrusel-deportivo',
    entityPhoto: 'https://readdy.ai/api/search-image?query=Sports%20radio%20talk%20show%20studio%20with%20microphones%20sports%20memorabilia%20dynamic%20atmosphere%20warm%20lighting%20celebration%20editorial%20photography&width=400&height=300&seq=ann-prog-carrusel&orientation=landscape',
    years: 10,
    anniversaryDate: '2026-06-21',
    anniversaryDateFormatted: '21 de Junio',
    foundedDate: '2016-06-21',
    description: 'Una década de deporte, pasión y polémica. El programa que une a la hinchada.',
    felicitaciones: 567,
    compartidos: 134,
    comentarios: 78,
    comments: [
      { id: 'c5', userName: 'Felipe Torres', userPhoto: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20young%20Peruvian%20male%20late%20twenties%20casual%20confident%20expression%20clean%20background%20editorial%20lighting&width=100&height=100&seq=ann-comment-ft&orientation=squarish', text: '10 años de la mejor mesa deportiva. ¡Salud, cracks!', dateRelative: 'Hace 3 horas' },
      { id: 'c6', userName: 'Juan Pablo Mejía', userPhoto: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20young%20Dominican%20male%20late%20twenties%20charismatic%20smile%20clean%20studio%20background%20editorial%20photography&width=100&height=100&seq=ann-comment-jpm&orientation=squarish', text: 'No me pierdo un solo programa. Feliz década, muchachos.', dateRelative: 'Hace 2 horas' },
    ],
    relatedInfo: 'Deportes · Lunes a Viernes 18:00',
    relatedLink: '/program/carrusel-deportivo',
    isToday: true,
    isThisWeek: true,
    isThisMonth: true,
    daysUntil: 0,
    hasCelebrated: false,
  },
  {
    id: 'ann-p-002',
    entityType: 'programa',
    entityName: 'Noches de Rock',
    entitySlug: 'noches-de-rock',
    entityPhoto: 'https://readdy.ai/api/search-image?query=Rock%20radio%20show%20dark%20moody%20studio%20with%20guitar%20amps%20vintage%20vinyl%20records%20warm%20amber%20light%20celebration%20atmosphere%20editorial%20photography&width=400&height=300&seq=ann-prog-nochesrock&orientation=landscape',
    years: 5,
    anniversaryDate: '2026-06-25',
    anniversaryDateFormatted: '25 de Junio',
    foundedDate: '2021-06-25',
    description: '5 años de puro rock. El programa que te acompaña cada noche con los mejores clásicos.',
    felicitaciones: 234,
    compartidos: 67,
    comentarios: 34,
    comments: [],
    relatedInfo: 'Rock · Martes y Jueves 22:00',
    relatedLink: '/program/noches-de-rock',
    isToday: false,
    isThisWeek: true,
    isThisMonth: true,
    daysUntil: 4,
    hasCelebrated: false,
  },
  {
    id: 'ann-p-003',
    entityType: 'programa',
    entityName: 'Despertando Juntos',
    entitySlug: 'despertando-juntos',
    entityPhoto: 'https://readdy.ai/api/search-image?query=Morning%20radio%20show%20bright%20cheerful%20studio%20with%20coffee%20cups%20sunrise%20light%20warm%20welcoming%20atmosphere%20celebration%20editorial%20photography&width=400&height=300&seq=ann-prog-despertando&orientation=landscape',
    years: 8,
    anniversaryDate: '2026-07-02',
    anniversaryDateFormatted: '2 de Julio',
    foundedDate: '2018-07-02',
    description: '8 años siendo tu despertador favorito. El morning show que te arranca una sonrisa.',
    felicitaciones: 456,
    compartidos: 98,
    comentarios: 56,
    comments: [],
    relatedInfo: 'Magazine · Lunes a Viernes 06:00',
    relatedLink: '/program/despertando-juntos',
    isToday: false,
    isThisWeek: false,
    isThisMonth: true,
    daysUntil: 11,
    hasCelebrated: false,
  },
  {
    id: 'ann-p-004',
    entityType: 'programa',
    entityName: 'Crea tu Programa',
    entitySlug: 'crea-tu-programa',
    entityPhoto: 'https://readdy.ai/api/search-image?query=Creative%20community%20radio%20show%20colorful%20studio%20with%20audience%20participation%20vibrant%20energy%20celebration%20atmosphere%20editorial%20photography&width=400&height=300&seq=ann-prog-crea&orientation=landscape',
    years: 2,
    anniversaryDate: '2026-06-23',
    anniversaryDateFormatted: '23 de Junio',
    foundedDate: '2024-06-23',
    description: '2 años dando voz a la comunidad. El programa donde vos sos el protagonista.',
    felicitaciones: 112,
    compartidos: 45,
    comentarios: 23,
    comments: [],
    relatedInfo: 'Participativo · Sábados 15:00',
    relatedLink: '/program/crea-tu-programa',
    isToday: false,
    isThisWeek: true,
    isThisMonth: true,
    daysUntil: 2,
    hasCelebrated: false,
  },

  // ---- LOCUTORES ----
  {
    id: 'ann-l-001',
    entityType: 'locutor',
    entityName: 'Alejandro "El Monstruo" Ramírez',
    entitySlug: 'alejandro-el-monstruo-ramirez',
    entityPhoto: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20charismatic%20radio%20host%20mid%20forties%20warm%20engaging%20smile%20broadcast%20studio%20background%20confident%20personality%20editorial%20photography&width=400&height=300&seq=ann-locutor-monstruo&orientation=landscape',
    years: 15,
    anniversaryDate: '2026-06-21',
    anniversaryDateFormatted: '21 de Junio',
    foundedDate: '2011-06-21',
    description: '15 años al aire. La voz más reconocida de la radio juvenil latinoamericana.',
    felicitaciones: 1204,
    compartidos: 289,
    comentarios: 167,
    comments: [
      { id: 'c7', userName: 'Ana Sofía Martínez', userPhoto: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20young%20Mexican%20woman%20mid%20twenties%20bright%20genuine%20smile%20studio%20background%20natural%20lighting%20warm%20tones&width=100&height=100&seq=ann-comment-asm&orientation=squarish', text: 'El Monstruo es leyenda. Crecí escuchándolo. ¡Felices 15 años al aire!', dateRelative: 'Hace 1 hora' },
    ],
    relatedInfo: 'La Mega 107.3 FM · Carrusel Deportivo',
    relatedLink: '/host/alejandro-el-monstruo-ramirez',
    isToday: true,
    isThisWeek: true,
    isThisMonth: true,
    daysUntil: 0,
    hasCelebrated: false,
  },
  {
    id: 'ann-l-002',
    entityType: 'locutor',
    entityName: 'María Clara Botero',
    entitySlug: 'maria-clara-botero',
    entityPhoto: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20elegant%20radio%20host%20woman%20late%20thirties%20warm%20sophisticated%20smile%20broadcast%20studio%20background%20confident%20presence%20editorial%20photography&width=400&height=300&seq=ann-locutor-mariaclara&orientation=landscape',
    years: 11,
    anniversaryDate: '2026-06-27',
    anniversaryDateFormatted: '27 de Junio',
    foundedDate: '2015-06-27',
    description: '11 años de periodismo con corazón. La voz que informa, analiza y emociona.',
    felicitaciones: 678,
    compartidos: 145,
    comentarios: 89,
    comments: [],
    relatedInfo: 'Onda Cero 104.5 FM · Despertando Juntos',
    relatedLink: '/host/maria-clara-botero',
    isToday: false,
    isThisWeek: true,
    isThisMonth: true,
    daysUntil: 6,
    hasCelebrated: false,
  },
  {
    id: 'ann-l-003',
    entityType: 'locutor',
    entityName: 'DJ ElectroBeat',
    entitySlug: 'dj-electrobeat',
    entityPhoto: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20young%20DJ%20male%20late%20twenties%20trendy%20style%20headphones%20around%20neck%20neon%20lit%20studio%20background%20energetic%20modern%20personality%20editorial%20photography&width=400&height=300&seq=ann-locutor-djeb&orientation=landscape',
    years: 4,
    anniversaryDate: '2026-06-29',
    anniversaryDateFormatted: '29 de Junio',
    foundedDate: '2022-06-29',
    description: '4 años mezclando beats y revolucionando las noches de radio.',
    felicitaciones: 345,
    compartidos: 98,
    comentarios: 54,
    comments: [],
    relatedInfo: 'ElectroBeat FM · Noches de Rock',
    relatedLink: '/host/dj-electrobeat',
    isToday: false,
    isThisWeek: true,
    isThisMonth: true,
    daysUntil: 8,
    hasCelebrated: false,
  },
  {
    id: 'ann-l-004',
    entityType: 'locutor',
    entityName: 'Patricia Solano',
    entitySlug: 'patricia-solano',
    entityPhoto: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20warm%20radio%20host%20woman%20early%20forties%20genuine%20approachable%20smile%20broadcast%20studio%20editorial%20photography%20experienced%20confident%20personality&width=400&height=300&seq=ann-locutor-patricia&orientation=landscape',
    years: 20,
    anniversaryDate: '2026-07-08',
    anniversaryDateFormatted: '8 de Julio',
    foundedDate: '2006-07-08',
    description: 'Dos décadas detrás del micrófono. Un referente indiscutible del periodismo radial.',
    felicitaciones: 1890,
    compartidos: 412,
    comentarios: 234,
    comments: [],
    relatedInfo: 'Radio Éxito 94.3 FM',
    relatedLink: '/host/patricia-solano',
    isToday: false,
    isThisWeek: false,
    isThisMonth: true,
    daysUntil: 17,
    hasCelebrated: false,
  },

  // ---- CLUBES ----
  {
    id: 'ann-c-001',
    entityType: 'club',
    entityName: 'Club Fans La Mega',
    entitySlug: 'club-fans-la-mega',
    entityPhoto: 'https://readdy.ai/api/search-image?query=Large%20community%20fan%20club%20gathering%20celebration%20colorful%20decorations%20unity%20joyful%20crowd%20warm%20evening%20light%20festival%20atmosphere%20editorial%20photography&width=400&height=300&seq=ann-club-lamega&orientation=landscape',
    years: 6,
    anniversaryDate: '2026-06-21',
    anniversaryDateFormatted: '21 de Junio',
    foundedDate: '2020-06-21',
    description: '6 años de la hinchada más grande de RFW. 12,400 miembros que son pura pasión.',
    felicitaciones: 723,
    compartidos: 189,
    comentarios: 112,
    comments: [
      { id: 'c8', userName: 'Isabella Vargas', userPhoto: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20young%20Venezuelan%20woman%20early%20twenties%20bright%20engaging%20smile%20clean%20studio%20background%20warm%20lighting&width=100&height=100&seq=ann-comment-iv&orientation=squarish', text: 'El mejor club de RFW. ¡Felices 6 años, familia!', dateRelative: 'Hace 45 min' },
    ],
    relatedInfo: '12,400 miembros · 18 retos completados',
    relatedLink: '/clubes/club-fans-la-mega',
    isToday: true,
    isThisWeek: true,
    isThisMonth: true,
    daysUntil: 0,
    hasCelebrated: false,
  },
  {
    id: 'ann-c-002',
    entityType: 'club',
    entityName: 'Rockeros Unidos',
    entitySlug: 'rockeros-unidos',
    entityPhoto: 'https://readdy.ai/api/search-image?query=Rock%20music%20community%20gathering%20with%20band%20merchandise%20guitar%20picks%20dark%20moody%20lighting%20celebration%20atmosphere%20passionate%20fans%20editorial%20photography&width=400&height=300&seq=ann-club-rockeros&orientation=landscape',
    years: 3,
    anniversaryDate: '2026-07-01',
    anniversaryDateFormatted: '1 de Julio',
    foundedDate: '2023-07-01',
    description: '3 años de rock, comunidad y buena música. 8,900 rockeros que hacen ruido juntos.',
    felicitaciones: 278,
    compartidos: 76,
    comentarios: 41,
    comments: [],
    relatedInfo: '8,900 miembros · 12 retos completados',
    relatedLink: '/clubes/rockeros-unidos',
    isToday: false,
    isThisWeek: false,
    isThisMonth: true,
    daysUntil: 10,
    hasCelebrated: false,
  },
  {
    id: 'ann-c-003',
    entityType: 'club',
    entityName: 'Comunidad Tropical',
    entitySlug: 'comunidad-tropical',
    entityPhoto: 'https://readdy.ai/api/search-image?query=Tropical%20music%20community%20dancing%20celebration%20colorful%20outfits%20beach%20setting%20joyful%20atmosphere%20sunset%20light%20festive%20editorial%20photography&width=400&height=300&seq=ann-club-tropical&orientation=landscape',
    years: 2,
    anniversaryDate: '2026-06-26',
    anniversaryDateFormatted: '26 de Junio',
    foundedDate: '2024-06-26',
    description: '2 años de sabor y baile. 5,200 miembros que llevan el ritmo en la sangre.',
    felicitaciones: 145,
    compartidos: 42,
    comentarios: 23,
    comments: [],
    relatedInfo: '5,200 miembros · 7 retos completados',
    relatedLink: '/clubes/comunidad-tropical',
    isToday: false,
    isThisWeek: true,
    isThisMonth: true,
    daysUntil: 5,
    hasCelebrated: false,
  },

  // ---- USUARIOS ----
  {
    id: 'ann-u-001',
    entityType: 'usuario',
    entityName: 'María José Herrera',
    entitySlug: 'maria-jose-herrera',
    entityPhoto: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20young%20Latina%20woman%20mid%20twenties%20warm%20friendly%20smile%20birthday%20celebration%20mood%20clean%20studio%20background%20soft%20natural%20lighting&width=200&height=200&seq=ann-user-mj&orientation=squarish',
    years: 4,
    anniversaryDate: '2026-06-21',
    anniversaryDateFormatted: '21 de Junio',
    foundedDate: '2022-06-21',
    description: '4 años en RFW. Top Fan Diamante, racha de 97 días y un corazón que late por cada radio.',
    felicitaciones: 245,
    compartidos: 67,
    comentarios: 43,
    comments: [
      { id: 'c9', userName: 'Carlos Vega', userPhoto: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20young%20Colombian%20male%20late%20twenties%20focused%20expression%20clean%20studio%20background%20warm%20lighting&width=100&height=100&seq=ann-comment-cv2&orientation=squarish', text: '¡Feliz aniversario en RFW, MJ! Sos una leyenda de esta comunidad.', dateRelative: 'Hace 2 horas' },
      { id: 'c10', userName: 'Valentina Ruiz', userPhoto: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20young%20Latina%20woman%20early%20twenties%20bright%20natural%20smile%20clean%20studio%20lighting&width=100&height=100&seq=ann-comment-vr2&orientation=squarish', text: '4 años y cada día más crack. ¡Abrazo enorme!', dateRelative: 'Hace 1 hora' },
    ],
    relatedInfo: 'Nivel 42 · Top Fan Diamante · VIP',
    relatedLink: '/oyente/maria-jose-herrera',
    isToday: true,
    isThisWeek: true,
    isThisMonth: true,
    daysUntil: 0,
    hasCelebrated: false,
  },
  {
    id: 'ann-u-002',
    entityType: 'usuario',
    entityName: 'Carlos Andrés Vega',
    entitySlug: 'carlos-andres-vega',
    entityPhoto: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20young%20Colombian%20male%20late%20twenties%20focused%20expression%20birthday%20celebration%20mood%20clean%20studio%20background%20warm%20lighting&width=200&height=200&seq=ann-user-cv&orientation=squarish',
    years: 3,
    anniversaryDate: '2026-06-24',
    anniversaryDateFormatted: '24 de Junio',
    foundedDate: '2023-06-24',
    description: '3 años de pura constancia. Top Fan Platino, racha de 54 días y siempre presente.',
    felicitaciones: 178,
    compartidos: 45,
    comentarios: 28,
    comments: [],
    relatedInfo: 'Nivel 38 · Top Fan Platino · VIP',
    relatedLink: '/oyente/carlos-andres-vega',
    isToday: false,
    isThisWeek: true,
    isThisMonth: true,
    daysUntil: 3,
    hasCelebrated: false,
  },
  {
    id: 'ann-u-003',
    entityType: 'usuario',
    entityName: 'Ana Sofía Martínez',
    entitySlug: 'ana-sofia-martinez',
    entityPhoto: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20young%20Mexican%20woman%20mid%20twenties%20bright%20genuine%20smile%20birthday%20celebration%20mood%20studio%20background%20natural%20lighting%20warm%20tones&width=200&height=200&seq=ann-user-asm&orientation=squarish',
    years: 2,
    anniversaryDate: '2026-06-26',
    anniversaryDateFormatted: '26 de Junio',
    foundedDate: '2024-06-26',
    description: '2 años en la familia RFW. Nivel 31, VIP, y siempre con una sonrisa en la cabina.',
    felicitaciones: 98,
    compartidos: 31,
    comentarios: 19,
    comments: [],
    relatedInfo: 'Nivel 31 · VIP · Racha 18 días',
    relatedLink: '/oyente/ana-sofia-martinez',
    isToday: false,
    isThisWeek: true,
    isThisMonth: true,
    daysUntil: 5,
    hasCelebrated: false,
  },
  {
    id: 'ann-u-004',
    entityType: 'usuario',
    entityName: 'Sebastián Gómez',
    entitySlug: 'sebastian-gomez',
    entityPhoto: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20young%20Argentine%20male%20early%20twenties%20relaxed%20natural%20expression%20birthday%20mood%20clean%20studio%20lighting%20editorial%20photography&width=200&height=200&seq=ann-user-sg&orientation=squarish',
    years: 1,
    anniversaryDate: '2026-06-22',
    anniversaryDateFormatted: '22 de Junio',
    foundedDate: '2025-06-22',
    description: '¡Primer aniversario en RFW! Un año de descubrir radios, ganar insignias y hacer amigos.',
    felicitaciones: 67,
    compartidos: 18,
    comentarios: 12,
    comments: [],
    relatedInfo: 'Nivel 14 · Racha 5 días',
    relatedLink: '/oyente/sebastian-gomez',
    isToday: false,
    isThisWeek: true,
    isThisMonth: true,
    daysUntil: 1,
    hasCelebrated: false,
  },
  {
    id: 'ann-u-005',
    entityType: 'usuario',
    entityName: 'Diego Fernando López',
    entitySlug: 'diego-fernando-lopez',
    entityPhoto: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20Argentine%20male%20early%20thirties%20relaxed%20confident%20expression%20birthday%20celebration%20clean%20studio%20background%20modern%20lighting&width=200&height=200&seq=ann-user-dfl&orientation=squarish',
    years: 5,
    anniversaryDate: '2026-07-03',
    anniversaryDateFormatted: '3 de Julio',
    foundedDate: '2021-07-03',
    description: '5 años de fidelidad a RFW. Nivel 28, testigo de la evolución de la plataforma.',
    felicitaciones: 312,
    compartidos: 89,
    comentarios: 54,
    comments: [],
    relatedInfo: 'Nivel 28 · Racha 23 días',
    relatedLink: '/oyente/diego-fernando-lopez',
    isToday: false,
    isThisWeek: false,
    isThisMonth: true,
    daysUntil: 12,
    hasCelebrated: false,
  },
];

export const anniversaryStats: AnniversaryStats = {
  totalToday: 5,
  totalThisWeek: 16,
  totalThisMonth: 22,
  totalUpcoming: 17,
  totalCelebratedThisYear: 847,
  mostCelebratedToday: 'Alejandro "El Monstruo" Ramírez',
  totalFelicitacionesHoy: 2340,
};

export const anniversaryRules: AnniversaryRule[] = [
  {
    id: 1,
    title: 'Totalmente automático',
    description: 'RFW detecta automáticamente cada aniversario. Radios, programas, locutores, clubes y usuarios — no hay que configurar nada. Cuando se acerca la fecha, el sistema lo celebra solo. El oyente solo tiene que felicitar, comentar y compartir.',
    icon: 'ri-sparkling-line',
    color: 'text-rose-600',
    bgColor: 'bg-rose-50',
  },
  {
    id: 2,
    title: 'Celebración en cadena',
    description: 'Cuando alguien felicita, se dispara una notificación suave a los contactos cercanos. Así la celebración crece orgánicamente. Cada felicitación suma puntos de comunidad y fortalece el vínculo entre oyentes y entidades.',
    icon: 'ri-links-line',
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
  },
  {
    id: 3,
    title: 'Sin spam, con corazón',
    description: 'Las notificaciones de aniversario son limitadas y relevantes. Solo ves aniversarios de entidades que seguís, clubes donde estás, y usuarios con los que interactuás. Cada celebración incluye contexto real: cuántos años, por qué es especial, y una foto que cuenta la historia.',
    icon: 'ri-heart-line',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
  },
];

export function getAnniversariesByType(type: AnniversaryEntityType | 'todas'): AnniversaryEntry[] {
  if (type === 'todas') return anniversaries;
  return anniversaries.filter((a) => a.entityType === type);
}

export function getTodayAnniversaries(): AnniversaryEntry[] {
  return anniversaries.filter((a) => a.isToday);
}

export function getThisWeekAnniversaries(): AnniversaryEntry[] {
  return anniversaries.filter((a) => a.isThisWeek).sort((a, b) => a.daysUntil - b.daysUntil);
}

export function getThisMonthAnniversaries(): AnniversaryEntry[] {
  return anniversaries.filter((a) => a.isThisMonth).sort((a, b) => a.daysUntil - b.daysUntil);
}

export function getUpcomingAnniversaries(limit: number = 10): AnniversaryEntry[] {
  return anniversaries.filter((a) => !a.isToday && a.isThisWeek).sort((a, b) => a.daysUntil - b.daysUntil).slice(0, limit);
}

export function getTypeDistribution(): { type: AnniversaryEntityType; today: number; thisWeek: number; total: number }[] {
  return anniversaryTypeConfigs.map((cfg) => ({
    type: cfg.key,
    today: anniversaries.filter((a) => a.entityType === cfg.key && a.isToday).length,
    thisWeek: anniversaries.filter((a) => a.entityType === cfg.key && a.isThisWeek).length,
    total: anniversaries.filter((a) => a.entityType === cfg.key).length,
  }));
}

export function getMostCelebrated(limit: number = 5): AnniversaryEntry[] {
  return [...anniversaries].sort((a, b) => b.felicitaciones - a.felicitaciones).slice(0, limit);
}